<template>
  <v-card
    class="tab-content"
    elevation="0"
    :disabled="isCardDisabled"
    v-disable-children="isCardDisabled"
  >
    <v-row class="mt-0">
      <v-col cols="6" class="py-0">
        <h3 class="mt-0">Образование</h3>

        <v-autocomplete
          class="required"
          v-model="form.eduLevel"
          hide-no-data
          :items="eduLevels"
          item-text="name"
          label="Уровень образования"
          placeholder="Выберите из списка"
          return-object
          :error-messages="errorMessages.eduLevel"
          @input="$v.form.eduLevel.$touch()"
          @blur="$v.form.eduLevel.$touch()"
        />

        <div class="d-flex">
          <v-autocomplete
            v-if="!form.eduSchoolCheck"
            class="required"
            v-model="form.eduSchool"
            hide-no-data
            :items="highSchools"
            item-text="shortName"
            label="Учебное заведение"
            placeholder="Выберите из списка"
            return-object
            :error-messages="errorMessages.eduSchool"
            @input="$v.form.eduSchool.$touch()"
            @blur="$v.form.eduSchool.$touch()"
          />

          <v-text-field
            v-else
            class="required"
            label="Введите учебное заведение"
            v-model="form.eduSchoolOutList"
            :error-messages="errorMessages.eduSchoolOutList"
            @input="$v.form.eduSchoolOutList.$touch()"
            @blur="$v.form.eduSchoolOutList.$touch()"
          />

          <v-checkbox
            v-model="form.eduSchoolCheck"
            class="ml-4 mt-6"
            label="Не найдено"
            hide-details
          />
        </div>

        <v-autocomplete
          class="required"
          v-model="form.eduYear"
          hide-no-data
          :items="eduYears"
          item-text="name"
          label="Год окончания"
          placeholder="Выберите из списка"
          return-object
          :error-messages="errorMessages.eduYear"
          @input="$v.form.eduYear.$touch()"
          @blur="$v.form.eduYear.$touch()"
        />

        <h3>Документ об образовании</h3>

        <v-autocomplete
          class="required"
          v-model="form.eduCardType"
          hide-no-data
          :items="diplomaTypes"
          item-text="description"
          label="Тип документа"
          placeholder="Выберите из списка"
          return-object
          :error-messages="errorMessages.eduCardType"
          @input="$v.form.eduCardType.$touch()"
          @blur="$v.form.eduCardType.$touch()"
        />

        <v-text-field
          :class="{ required: form.isDiploma }"
          label="Серия"
          v-model="form.eduCardSeries"
          :error-messages="errorMessages.eduCardSeries"
          @input="$v.form.eduCardSeries.$touch()"
          @blur="$v.form.eduCardSeries.$touch()"
        />

        <v-text-field
          class="required"
          label="Номер"
          v-model="form.eduCardNumber"
          hide-details
          :error-messages="errorMessages.eduCardNumber"
          @input="$v.form.eduCardNumber.$touch()"
          @blur="$v.form.eduCardNumber.$touch()"
        />

        <v-checkbox
          class="equal-places"
          label="Место выдачи совпадает с учебным заведением"
          hide-details
          @change="form.copyEduSchoolInfo($event)"
        />

        <div class="d-flex">
          <v-autocomplete
            v-if="!form.eduFirstSpecCenterCheck"
            v-model="form.eduFirstSpecCenter"
            hide-no-data
            :items="highSchools"
            item-text="shortName"
            label="Кем выдано"
            placeholder="Выберите из списка"
            return-object
          />

          <v-text-field
            v-if="form.eduFirstSpecCenterCheck"
            label="Введите учебное заведение"
            v-model="form.eduFirstSpecCenterOutList"
            :error-messages="errorMessages.eduFirstSpecCenterOutList"
            @input="$v.form.eduFirstSpecCenterOutList.$touch()"
            @blur="$v.form.eduFirstSpecCenterOutList.$touch()"
          />

          <v-checkbox
            v-model="form.eduFirstSpecCenterCheck"
            class="ml-4 mt-6"
            label="Не найдено"
            hide-details
          />
        </div>

        <v-text-field
          class="required"
          label="Дата выдачи документа"
          v-model="form.eduCardIssueDate"
          v-mask="'##.##.####'"
          :error-messages="errorMessages.eduCardIssueDate"
          @input="$v.form.eduCardIssueDate.$touch()"
          @blur="$v.form.eduCardIssueDate.$touch()"
        />

        <h3>Специальность по диплому</h3>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-autocomplete
              class="required"
              v-model="form.eduSpecialtySpc"
              hide-no-data
              :items="specialitySpc"
              item-text="specialtyName"
              label="Специалитет"
              placeholder="Выберите из списка"
              return-object
              :error-messages="errorMessages.eduSpecialtySpc"
              @input="$v.form.eduSpecialtySpc.$touch()"
              @blur="$v.form.eduSpecialtySpc.$touch()"
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <span>
            При выборе уровня образования "Специалитет" обязательным является <br />
            заполнение полей "Ординатура", "Интернатура", или "ДПО"
          </span>
        </v-tooltip>
        <div class="d-flex flex-column ml-4" v-if="form.eduSpecialtySpc">
          <v-autocomplete
            class="required"
            v-model="form.eduSpecialtySpcEducationDocType"
            hide-no-data
            :items="educationDocType"
            return-object
            placeholder="Выберите из списка"
            label="Наименование документа"
            item-text="name"
            :error-messages="errorMessages.eduSpecialtySpcEducationDocType"
            @input="$v.form.eduSpecialtySpcEducationDocType.$touch()"
            @blur="$v.form.eduSpecialtySpcEducationDocType.$touch()"
          />
          <v-autocomplete
            class="required"
            v-model="form.eduSpecialtySpcYear"
            hide-no-data
            :items="eduYears"
            item-text="name"
            label="Год окончания"
            placeholder="Выберите из списка"
            return-object
            :error-messages="errorMessages.eduSpecialtySpcYear"
            @input="$v.form.eduSpecialtySpcYear.$touch()"
            @blur="$v.form.eduSpecialtySpcYear.$touch()"
          />
        </div>

        <v-autocomplete
          v-model="form.eduSpecialtyInt"
          hide-no-data
          :items="specialityIntOrd"
          item-text="description"
          label="Интернатура"
          placeholder="Выберите из списка"
          return-object
        />

        <div class="d-flex flex-column ml-4" v-if="form.eduSpecialtyInt">
          <v-autocomplete
            class="required"
            v-model="form.eduSpecialtyIntEducationDocType"
            hide-no-data
            :items="educationDocType"
            return-object
            placeholder="Выберите из списка"
            label="Наименование документа"
            item-text="name"
            :error-messages="errorMessages.eduSpecialtyIntEducationDocType"
            @input="$v.form.eduSpecialtyIntEducationDocType.$touch()"
            @blur="$v.form.eduSpecialtyIntEducationDocType.$touch()"
          />
          <v-autocomplete
            class="required"
            v-model="form.eduSpecialtyIntYear"
            hide-no-data
            :items="eduYears"
            item-text="name"
            label="Год окончания"
            placeholder="Выберите из списка"
            return-object
            :error-messages="errorMessages.eduSpecialtyIntYear"
            @input="$v.form.eduSpecialtyIntYear.$touch()"
            @blur="$v.form.eduSpecialtyIntYear.$touch()"
          />
        </div>

        <v-autocomplete
          v-model="form.eduSpecialtyOrd"
          hide-no-data
          :items="specialityIntOrd"
          item-text="description"
          label="Ординатура"
          placeholder="Выберите из списка"
          return-object
        />

        <div class="d-flex flex-column ml-4" v-if="form.eduSpecialtyOrd">
          <v-autocomplete
            class="required"
            v-model="form.eduSpecialtyOrdEducationDocType"
            hide-no-data
            :items="educationDocType"
            return-object
            placeholder="Выберите из списка"
            label="Наименование документа"
            item-text="name"
            :error-messages="errorMessages.eduSpecialtyOrdEducationDocType"
            @input="$v.form.eduSpecialtyOrdEducationDocType.$touch()"
            @blur="$v.form.eduSpecialtyOrdEducationDocType.$touch()"
          />
          <v-autocomplete
            class="required"
            v-model="form.eduSpecialtyOrdYear"
            hide-no-data
            :items="eduYears"
            item-text="name"
            label="Год окончания"
            placeholder="Выберите из списка"
            return-object
            :error-messages="errorMessages.eduSpecialtyOrdYear"
            @input="$v.form.eduSpecialtyOrdYear.$touch()"
            @blur="$v.form.eduSpecialtyOrdYear.$touch()"
          />
        </div>

        <v-autocomplete
          class="required"
          v-model="form.eduSpecialtyDpo"
          hide-no-data
          :items="specialityDpo"
          item-text="specialtyName"
          label="Дополнительное профессиональное образование"
          placeholder="Выберите из списка"
          return-object
          :error-messages="errorMessages.eduSpecialtyDpo"
          @input="$v.form.eduSpecialtyDpo.$touch()"
          @blur="$v.form.eduSpecialtyDpo.$touch()"
        />
      </v-col>

      <v-col cols="6" class="py-0">
        <h3 class="mt-0">Научная квалификация</h3>

        <v-autocomplete
          class="required"
          v-model="form.eduAcademicDegree"
          hide-no-data
          :items="academicDegrees"
          item-text="name"
          label="Степень"
          placeholder="Выберите из списка"
          return-object
          :error-messages="errorMessages.eduAcademicDegree"
          @input="$v.form.eduAcademicDegree.$touch()"
          @blur="$v.form.eduAcademicDegree.$touch()"
        />

        <v-autocomplete
          v-model="form.eduAcademicCategory"
          hide-no-data
          :items="academicCategories"
          item-text="name"
          label="Звание"
          placeholder="Выберите из списка"
          return-object
        />

        <v-autocomplete
          v-model="form.eduQualificationCategory"
          hide-no-data
          :items="categoryPersons"
          item-text="name"
          label="Квалификационная категория"
          placeholder="Выберите из списка"
          return-object
        />

        <h3>Индивидуальный номер мед. работника</h3>

        <v-text-field
          label="Номер мед. работника"
          v-model="form.indNumOfHealthWorker"
          :error-messages="errorMessages.indNumOfHealthWorker"
          @input="$v.form.indNumOfHealthWorker.$touch()"
          @blur="$v.form.indNumOfHealthWorker.$touch()"
        />

        <v-text-field
          label="Дата включения в реестр"
          v-model="form.dateInclude2Register"
          v-mask="'##.##.####'"
          :error-messages="errorMessages.dateInclude2Register"
          @input="$v.form.dateInclude2Register.$touch()"
          @blur="$v.form.dateInclude2Register.$touch()"
        />

        <v-radio-group v-model="form.isValidAccred" class="pt-0 mt-0 mb-4" row hide-details>
          <span class="label-span accent--text required-span">Свидетельство об аккредитации</span>
          <v-radio class="accent--text" label="Да" :value="true" />
          <v-radio class="accent--text" label="Нет" :value="false" />
        </v-radio-group>

        <div v-if="form.isValidAccred">
          <v-text-field
            v-model="form.validAccredGosNum"
            class="required"
            label="Государственный номер свидетельства"
            :error-messages="errorMessages.validAccredGosNum"
            @input="$v.form.validAccredGosNum.$touch()"
            @blur="$v.form.validAccredGosNum.$touch()"
          />

          <v-text-field
            v-model="form.validAccredRegNum"
            class="required"
            label="Регистрационный номер свидетельства"
            :error-messages="errorMessages.validAccredRegNum"
            @input="$v.form.validAccredRegNum.$touch()"
            @blur="$v.form.validAccredRegNum.$touch()"
          />

          <v-autocomplete
            class="required"
            v-model="form.specialityTable"
            hide-no-data
            :items="specialityDpo"
            item-text="specialtyName"
            label="Специальность"
            placeholder="Выберите из списка"
            return-object
            :error-messages="errorMessages.specialityTable"
            @input="$v.form.specialityTable.$touch()"
            @blur="$v.form.specialityTable.$touch()"
          />
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { catalogApi } from '@/api'
import _isEqual from 'lodash/isEqual'
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators'
import { dateFormat, setValidateMessages } from '@/scripts/validation'
import { Education } from '@/models'
import { Speciality } from '@/enums/speciality'

export default {
  name: 'ListenerCardEducation',

  props: {
    data: Object,
    tabEditability: Object,
  },

  async created() {
    await this.getDictionaries()

    this.setValidStatus()
  },

  data: () => ({
    eduLevels: [],
    highSchools: [],
    eduYears: [],
    diplomaTypes: [],
    specialitySpc: [],
    specialityIntOrd: [],
    specialityAsp: [],
    specialityDpo: [],
    academicDegrees: [],
    academicCategories: [],
    categoryPersons: [],
    educationDocType: [],
    //HERE: в массив необходимо добавить специальности, при которых поля specialityIntId(Интернатура) и specialityOrdId (Ординатура) будет необязательным
    //Пример:
    //IntOrdNotRequiredRelatedSpc:[Speciality.JuniorNurse],
    IntOrdNotRequiredRelatedSpc: [],

    form: new Education(),
  }),

  validations() {
    return {
      form: {
        eduLevel: { required },
        eduSchool: { required: requiredIf(() => !this.form.eduSchoolCheck) },
        eduSchoolOutList: {
          required: requiredIf(() => this.form.eduSchoolCheck),
          maxLength: maxLength(255),
        },
        eduYear: { required },
        eduSpecialtySpcYear: { required: requiredIf(() => this.form.eduSpecialtySpc) },
        eduSpecialtySpcEducationDocType: { required: requiredIf(() => this.form.eduSpecialtySpc) },
        eduSpecialtyIntYear: { required: requiredIf(() => this.form.eduSpecialtyInt) },
        eduSpecialtyIntEducationDocType: { required: requiredIf(() => this.form.eduSpecialtyInt) },
        eduSpecialtyOrdYear: { required: requiredIf(() => this.form.eduSpecialtyOrd) },
        eduSpecialtyOrdEducationDocType: { required: requiredIf(() => this.form.eduSpecialtyOrd) },

        eduCardType: { required },
        eduCardSeries: {
          required: requiredIf(() => this.form.isDiploma),
          maxLength: maxLength(50),
        },
        eduCardNumber: { required, maxLength: maxLength(50) },
        eduFirstSpecCenterOutList: { maxLength: maxLength(255) },
        eduCardIssueDate: { required, dateFormat },
        eduSpecialtySpc: { required },
        eduSpecialtyDpo: { required },
        // eduSpecialtyInt: { required: requiredIf(() => this.isIntOrdRequired) },
        // eduSpecialtyOrd: { required: requiredIf(() => this.isIntOrdRequired) },
        eduAcademicDegree: { required },
        indNumOfHealthWorker: { maxLength: maxLength(18) },
        dateInclude2Register: { dateFormat },

        validAccredGosNum: {
          required: requiredIf(() => this.form.isValidAccred),
          maxLength: maxLength(30),
        },
        validAccredRegNum: {
          required: requiredIf(() => this.form.isValidAccred),
          maxLength: maxLength(30),
        },
        specialityTable: { required: requiredIf(() => this.form.isValidAccred) },
      },
    }
  },

  computed: {
    isIntOrdRequired() {
      return !this.IntOrdNotRequiredRelatedSpc.some((el) => el === this.form.eduSpecialtySpc?.id)
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.form.$params).forEach((key) => {
        allErrors[key] = setValidateMessages(this.$v.form[key])
      })

      return allErrors
    },

    isCardDisabled() {
      return this.tabEditability?.eduTab || false
    },
  },

  methods: {
    async getDictionaries() {
      try {
        const [
          list,
          list2,
          list3,
          list4,
          list5,
          list6,
          list7,
          list8,
          list9,
          list10,
          list11,
          list12,
        ] = await Promise.all([
          catalogApi.getEduLevels(),
          catalogApi.getHighSchools(),
          catalogApi.getEduYears(),
          catalogApi.getDiplomaTypes(),

          catalogApi.getSpecialityTableByEduLevel(1),
          catalogApi.getSpeciality(2),
          catalogApi.getSpeciality(3),
          catalogApi.getSpecialityTable(),

          catalogApi.getAcademicDegrees(),
          catalogApi.getAcademicCategories(),
          catalogApi.getCategoryPersons(),
          catalogApi.getEducationDocType(),
        ])

        this.eduLevels = list
        this.highSchools = list2
        this.eduYears = list3
        this.diplomaTypes = list4
        this.specialitySpc = list5
        this.specialityIntOrd = list6
        this.specialityAsp = list7
        this.specialityDpo = list8
        this.academicDegrees = list9
        this.academicCategories = list10
        this.categoryPersons = list11
        this.educationDocType = list12

        this.$emit('dataLoaded')
      } catch (e) {
        console.log(e.status)
      }
    },

    setValidStatus() {
      this.$emit('setValidStatus', !this.$v.$invalid)
    },
  },

  watch: {
    data: {
      immediate: true,
      handler(val) {
        if (val) {
          if (!_isEqual(this.form, val)) {
            this.form = Education.buildFromAPI(val)
          }
        } else {
          this.form = new Education()
        }
      },
    },

    form: {
      deep: true,
      handler(data) {
        this.setValidStatus()
        this.$emit('updateEducationCard', data)
      },
    },
  },
}
</script>

<style scoped>
.equal-places {
  margin: 12px 0 16px -4px;
  padding-top: 0;
}

.label-span {
  width: 100%;
  margin-bottom: 8px;
  font-size: 14px;
  color: var(--accent-color) !important;
}
</style>
